import { Alert } from '@components/display/alerts';
import { ButtonLink } from '@components/utility/buttons';
import { AlertTitle, Typography } from '@mui/material';
import {
    ProductIdentificationDialog,
    ProductIdentificationDialogRef,
    ProductIdentificationMode,
} from '@rla/components/dialogs';
import { RLAFormMode } from '@rla/types';
import { useRef } from 'react';
import { FormattedMessage } from 'react-intl';

export const ProofOfOwnershipAlert = ({ mode }: { mode: RLAFormMode }) => {
    const productIdentificationDialogRef = useRef<ProductIdentificationDialogRef>(null);

    return (
        <Alert>
            <AlertTitle>
                <FormattedMessage id="rla.proofOfOwnershipRequired" defaultMessage="Proof of Ownership Required" />
            </AlertTitle>
            <Typography fontFamily="poppins" fontSize={12} color="light-toast-message" mb={2}>
                {mode === RLAFormMode.PIN_PUK_RECOVERY ? (
                    <FormattedMessage
                        id="rla.proofOfOwnershipRequired.pinpuk.optionA"
                        defaultMessage="<b>Option A:</b> To verify your request, we require the serial number and a clear photo of your <a>product's identification label</a>."
                        values={{
                            b: (chunks) => <b>{chunks}</b>,
                            a: (chunks) => (
                                <ButtonLink
                                    variant="inherit"
                                    className="align-baseline"
                                    onClick={() =>
                                        productIdentificationDialogRef.current?.setMode(ProductIdentificationMode.LABEL)
                                    }
                                >
                                    {chunks}
                                </ButtonLink>
                            ),
                        }}
                    />
                ) : (
                    <FormattedMessage
                        id="rla.proofOfOwnershipRequired.vrm.optionA"
                        defaultMessage="<b>Option A:</b> To verify your request, we require the serial number and a clear photo of your <a>product's identification label</a> located on the black metal housing on the side of your unit."
                        values={{
                            b: (chunks) => <b>{chunks}</b>,
                            a: (chunks) => (
                                <ButtonLink
                                    variant="inherit"
                                    className="align-baseline"
                                    onClick={() =>
                                        productIdentificationDialogRef.current?.setMode(ProductIdentificationMode.LABEL)
                                    }
                                >
                                    {chunks}
                                </ButtonLink>
                            ),
                        }}
                    />
                )}
            </Typography>
            <Typography fontFamily="poppins" fontSize={12} color="light-toast-message">
                {mode === RLAFormMode.PIN_PUK_RECOVERY ? (
                    <FormattedMessage
                        id="rla.proofOfOwnershipRequired.pinpuk.optionB"
                        defaultMessage="<b>Option B:</b> <semibold>If your unit is built-in or the label is inaccessible</semibold>, please provide a photo of the unit as it is currently installed in your system 
    and a screenshot from the <a>VictronConnect App</a> showing the specific Serial Number for this device."
                        values={{
                            b: (chunks) => <b>{chunks}</b>,
                            semibold: (chunks) => <span style={{ fontWeight: 500 }}>{chunks}</span>,
                            a: (chunks) => (
                                <ButtonLink
                                    variant="inherit"
                                    className="align-baseline"
                                    onClick={() =>
                                        productIdentificationDialogRef.current?.setMode(ProductIdentificationMode.APP)
                                    }
                                >
                                    {chunks}
                                </ButtonLink>
                            ),
                        }}
                    />
                ) : (
                    <FormattedMessage
                        id="rla.proofOfOwnershipRequired.vrm.optionB"
                        defaultMessage="<b>Option B:</b> <semibold>If your unit is built-in or the label is inaccessible</semibold>, please provide a photo of the unit as it is currently installed in your system 
    and a screenshot from the <a>VictronConnect App</a> or the device VRM screen showing the specific Serial Number or VRM ID for this device."
                        values={{
                            b: (chunks) => <b>{chunks}</b>,
                            semibold: (chunks) => <span style={{ fontWeight: 500 }}>{chunks}</span>,
                            a: (chunks) => (
                                <ButtonLink
                                    variant="inherit"
                                    className="align-baseline"
                                    onClick={() =>
                                        productIdentificationDialogRef.current?.setMode(ProductIdentificationMode.APP)
                                    }
                                >
                                    {chunks}
                                </ButtonLink>
                            ),
                        }}
                    />
                )}
            </Typography>
            <ProductIdentificationDialog ref={productIdentificationDialogRef} />
        </Alert>
    );
};
